import { useContext, useState } from 'react';
import './DetailsExpand.css';
import { Context } from '../../../../context/Context';
import { useRestaurantAccess } from "../../../../access/hooks/useRestaurantAccess";
import { DeleteModal } from '../../../modals/Delete';
import { translations } from "../../../../utils/lang";
import { COMP_FIELDS2 } from '../../../../utils/Compensation';
import { deleteCompAPI, updateApprovalByStoreDateAPI, updateCompAPI, updateCompByStoreDateAppAPI, updateDisapproveByStoreDateAppAPI } from '../../../../utils/api/report';
import { formatter } from '../../../../utils/helpers';
import { Button, Repeat, ThumbsDown, ThumbsUp, Trash2 } from '../../../ui';
import {
    branchRefsOf,
    getMonthKey,
    groupBy,
    groupTotals,
    pendingMarker,
    rowMonthKey,
    rowToggleProps,
    sortMonthEntries,
    stopRowToggle,
    storeIdsOf,
} from '../../reportUtils';
import { NodeState } from '../../reportNode';
import { getFieldLabel, getReportText } from '../../reportText';
import { MONTHLY_FIELDS as AR_FIELD_LABELS } from '../../../../utils/Monthly';

const SUM_KEYS = ['orderAmount', 'enduranceAmount', 'refundAmount'];

// `groups` (optional): server-side totals per month / app / branch for the store's whole filtered set. The headers print those instead of summing
// the rows they were given. Without `groups` (older backend) the headers sum `data`.
// `paged` (optional, the server-paged table): `data` then only carries the hierarchy levels (usePagedReport), the orders of the open branch arrive in
// `paged.leafRows`, and opening a level reports the new path through `paged.onNode`.
export const DetailsExpand = ({ data, onRefresh, groups = null, paged = null }) => {
    const { lang, handleTokenExpiration } = useContext(Context);
    const { has, canForAll } = useRestaurantAccess();
    const [expandedMonth, setExpandedMonth] = useState(null);
    const [expandedBranch, setExpandedBranch] = useState(null);
    const [expandedApp, setExpandedApp] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editItem, setEditItem] = useState({});
    // One mutation at a time; failures are shown instead of swallowed.
    const [pending, setPending] = useState(false);
    const [actionError, setActionError] = useState("");
    const text = getReportText(lang);

    const maps = {
        orderStatus: translations[lang].filter.orderStatus,
        app: translations[lang].filter.apps,
    };

    if (!data?.length) return null;
    // Group by "MonthName-YYYY" (explicitly parsed; see reportUtils).
    const groupedByMonth = groupBy(data, (item) => getMonthKey(item[COMP_FIELDS2.date]));

    // Keyed by the record id so the save always targets the record being edited.
    const handleEdit = (item) => {
        setIsEditing(item._id);
        setEditItem(item);
    };

    const handleCancel = () => {
        setIsEditing(null);
        setEditItem({});
    };

    const handleChange = (field, value) => {
        setEditItem((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const monthOf = (items) => rowMonthKey(items[0], 'compensation');

    const toggleMonth = (date, items) => {
        paged?.onNode(expandedMonth === date ? {} : { month: monthOf(items) });
        setExpandedMonth(prev => prev === date ? null : date);
        setExpandedApp(null);
        setExpandedBranch(null);
    };

    const toggleApp = (app, items) => {
        paged?.onNode({ month: monthOf(items) });
        setExpandedApp(prev => prev === app ? null : app);
        setExpandedBranch(null);
    };

    const toggleBranch = (branch, items) => {
        paged?.onNode(expandedBranch === branch ? { month: monthOf(items) } : { month: monthOf(items), app: items[0].app, branchRefs: branchRefsOf(items) });
        setExpandedBranch(prev => prev === branch ? null : branch);
    };
    const confirmDelete = (id) => {
        setDeleteTarget(id);
    };

    // Runs a mutation; the API helpers resolve to undefined on failure.
    const runMutation = async (request, onSuccess) => {
        if (pending) return false;
        const token = localStorage.getItem('auth-token');
        if (!token) {
            handleTokenExpiration();
            return false;
        }
        setPending(true);
        setActionError("");
        try {
            const result = await request(token);
            if (!result) {
                setActionError(text.actionFailed);
                return false;
            }
            onSuccess?.();
            onRefresh?.(); // refresh parent
            return true;
        } catch (err) {
            console.error('Details report update failed:', err);
            setActionError(text.actionFailed);
            return false;
        } finally {
            setPending(false);
        }
    };

    const handleConfirmedDeleteRaw = (id) =>
        runMutation((token) => deleteCompAPI(token, id), () => setDeleteTarget(null));

    // Saves the record that is being edited.
    const handleSaveRaw = () =>
        runMutation((token) => updateCompAPI(token, editItem._id, editItem), () => {
            setIsEditing(null);
            setEditItem({});
        });

    const approveDataRaw = (store, date, app, storeIds) =>
        runMutation((token) => updateCompByStoreDateAppAPI(token, store, date, app, storeIds));

    const approveByMonthRaw = (store, date, approve, storeIds) =>
        runMutation((token) =>
            updateApprovalByStoreDateAPI(token, "compensation", store, date, approve, storeIds));

    const disapproveDataRaw = (store, date, app, storeIds) =>
        runMutation((token) =>
            updateDisapproveByStoreDateAppAPI(token, "compensation", store, date, app, storeIds));



  // Handler-level authorization: re-checked right before every request, against
  // the restaurant(s) the record belongs to (report rows carry the restaurant _id,
  // resolved to the public id by useRestaurantAccess). Hidden buttons are not enough.
  const refusedMutation = Promise.resolve(false);
  const handleConfirmedDelete = (id) =>
    canForAll("report.entry.delete", [[...data, ...(paged?.leafRows || [])].find((row) => row._id === id)?.storeId])
      ? handleConfirmedDeleteRaw(id)
      : refusedMutation;
  const handleSave = () =>
    canForAll("report.entry.update", [editItem?.storeId]) ? handleSaveRaw() : refusedMutation;
  const approveData = (store, date, app, storeIds) =>
    canForAll("report.approve", storeIds) ? approveDataRaw(store, date, app, storeIds) : refusedMutation;
  const disapproveData = (store, date, app, storeIds) =>
    canForAll("report.approve", storeIds) ? disapproveDataRaw(store, date, app, storeIds) : refusedMutation;
  const approveByMonth = (store, date, approve, storeIds) =>
    canForAll("report.approve", storeIds) ? approveByMonthRaw(store, date, approve, storeIds) : refusedMutation;


    return (
        <div className="report-details-card-col">
            {actionError && (
                <p className="report-action-error" role="alert">{actionError}</p>
            )}
            {sortMonthEntries(groupedByMonth.entries(), paged?.ascending).map(([month, monthItems]) => {
                const apps = monthItems.reduce((acc, item) => {
                    const app = item[COMP_FIELDS2.app] || 'Unknown App';
                    acc[app] = acc[app] || [];
                    acc[app].push(item);
                    return acc;
                }, {});

                const monthSummary = monthItems.reduce((acc, item) => {
                    acc.count += 1;
                    acc.orderAmount += parseFloat(item[COMP_FIELDS2.orderAmount] || 0);
                    acc.enduranceAmount += parseFloat(item[COMP_FIELDS2.enduranceAmount] || 0);
                    acc.refundAmount += parseFloat(item[COMP_FIELDS2.refundAmount] || 0);
                    return acc;
                }, {
                    count: 0,
                    orderAmount: 0,
                    enduranceAmount: 0,
                    refundAmount: 0,
                });
                const monthTotals = groupTotals(groups, { month: rowMonthKey(monthItems[0], 'compensation') }, SUM_KEYS);
                if (monthTotals) Object.assign(monthSummary, monthTotals);
                const isMonthApproved = pendingMarker(monthTotals, monthItems.find(a => a.isApproved === false));
                return (
                    <div key={month}>
                        <div className={`report-details-summary-card month ${isMonthApproved && !isMonthApproved?.isApproved ? "notApproved" : ""}`} {...rowToggleProps(expandedMonth === month, () => toggleMonth(month, monthItems))}>
                            <div className="report-details-p center">
                                <p><strong>{translations[lang].filter.month[month.split("-")[0]] ?? month.split("-")[0]}-{month.split("-")[1]}</strong></p>
                                {isMonthApproved && <span className="report-pending-tag">{text.pendingApproval}</span>}
                            </div>
                            <div className="report-details-p">
                                {` ${monthSummary.count}`}
                            </div>
                            <div className="report-details-p">
                                {` ${formatter.format(monthSummary.orderAmount.toFixed(2))}`}
                            </div>
                            <div className="report-details-p">
                                {` ${formatter.format(monthSummary.enduranceAmount.toFixed(2))}`}
                            </div>
                            <div className="report-details-p">
                                {` ${formatter.format(monthSummary.refundAmount.toFixed(2))}`}
                            </div>
                            <div>
                                {has("report.approve") && (isMonthApproved && !isMonthApproved?.isApproved ?
                                    (<Button variant="danger"
            appearance="soft"
            
            rounded   icon={<ThumbsUp size={20} />}
            aria-label={text.approveMonth} title={text.approveMonth} disabled={pending}
            onClick={stopRowToggle(() => approveByMonth(monthItems[0].storeName, monthItems[0].date, true, storeIdsOf(monthItems)))}>
                                      
                                    </Button>) :
                                    (<Button variant="danger"
            appearance="soft"
            
            rounded   icon={<ThumbsDown size={20} />}
            aria-label={text.disapproveMonth} title={text.disapproveMonth} disabled={pending}
            onClick={stopRowToggle(() => approveByMonth(monthItems[0].storeName, monthItems[0].date, false, storeIdsOf(monthItems)))}>
                                       
                                    </Button>))
                                }
                            </div>
                        </div>

                        {expandedMonth === month && paged?.detailPending && <NodeState state={paged.detail} />}
                        {expandedMonth === month && !paged?.detailPending && (
                            <div className="expand-apps">
                                {Object.entries(apps).map(([app, appItems]) => {
                                    const branches = appItems.reduce((acc, item) => {
                                        const branch = item[COMP_FIELDS2.branchName] || 'Unknown Branch';
                                        acc[branch] = acc[branch] || [];
                                        acc[branch].push(item);
                                        return acc;
                                    }, {});

                                    const appSummary = appItems.reduce((acc, item) => {
                                        acc.count += 1;
                                        acc.orderAmount += parseFloat(item[COMP_FIELDS2.orderAmount] || 0);
                                        acc.enduranceAmount += parseFloat(item[COMP_FIELDS2.enduranceAmount] || 0);
                                        acc.refundAmount += parseFloat(item[COMP_FIELDS2.refundAmount] || 0);
                                        return acc;
                                    }, {
                                        count: 0,
                                        orderAmount: 0,
                                        enduranceAmount: 0,
                                        refundAmount: 0,
                                    });
                                    const appTotals = groupTotals(groups, { month: rowMonthKey(appItems[0], 'compensation'), app: appItems[0].app ?? null }, SUM_KEYS);
                                    if (appTotals) Object.assign(appSummary, appTotals);
                                    const isAppApproved = pendingMarker(appTotals, appItems.find(a => a.isApproved === false));

                                    return (
                                        <div key={app} className="expand-app-section">
                                            <div className={`report-details-summary-card app ${isAppApproved && !isAppApproved?.isApproved ? "notApproved" : ""}`} {...rowToggleProps(expandedApp === app, () => toggleApp(app, appItems))}>
                                                <div className="report-details-p center">
                                                    <p><strong>{maps.app?.[app] ?? translations[lang].notApp + " " + app[app.length - 1]}</strong></p>
                                                </div>
                                                <div className="report-details-p">
                                                    {` ${appSummary.count}`}
                                                </div>
                                                <div className="report-details-p">
                                                    {` ${formatter.format(appSummary.orderAmount.toFixed(2))}`}
                                                </div>
                                                <div className="report-details-p">
                                                    {` ${formatter.format(appSummary.enduranceAmount.toFixed(2))}`}
                                                </div>
                                                <div className="report-details-p">
                                                    {` ${formatter.format(appSummary.refundAmount.toFixed(2))}`}
                                                </div>
                                                <div>
                                                    {has("report.approve") && (isAppApproved && !isAppApproved?.isApproved ?
                                                        (<Button variant="danger"
            appearance="soft"
            
            rounded   icon={<ThumbsUp size={20} />}
            aria-label={text.approveApp} title={text.approveApp} disabled={pending}
            onClick={stopRowToggle(() => approveData(appItems[0].storeName, appItems[0].date, app, storeIdsOf(appItems)))}>
                                                        
                                                        </Button>) :
                                                        (<Button   variant="danger"
            appearance="soft"
            
            rounded   icon={<ThumbsDown size={20} />}
            aria-label={text.disapproveApp} title={text.disapproveApp} disabled={pending}
            onClick={stopRowToggle(() => disapproveData(appItems[0].storeName, appItems[0].date, app, storeIdsOf(appItems)))}>
                                                             
                                                        </Button>))
                                                    }
                                                </div>
                                            </div>

                                            {
                                                expandedApp === app && (
                                                    <div className="expand-apps">
                                                        {Object.entries(branches).map(([branch, branchItems]) => {
                                                            const orderIds = (paged ? paged.leafRows : branchItems).reduce((acc, item) => {
                                                                const orderId = item[COMP_FIELDS2.orderId] || 'Unknown Branch';
                                                                acc[orderId] = acc[orderId] || [];
                                                                acc[orderId].push(item);
                                                                return acc;
                                                            }, {});

                                                            const branchSummary = branchItems.reduce((acc, item) => {
                                                                acc.count += 1;
                                                                acc.orderAmount += parseFloat(item[COMP_FIELDS2.orderAmount] || 0);
                                                                acc.enduranceAmount += parseFloat(item[COMP_FIELDS2.enduranceAmount] || 0);
                                                                acc.refundAmount += parseFloat(item[COMP_FIELDS2.refundAmount] || 0);
                                                                return acc;
                                                            }, {
                                                                count: 0,
                                                                orderAmount: 0,
                                                                enduranceAmount: 0,
                                                                refundAmount: 0,
                                                            });
                                                            const branchTotals = groupTotals(groups, { month: rowMonthKey(branchItems[0], 'compensation'), app: branchItems[0].app ?? null, branchRefs: branchRefsOf(branchItems) }, SUM_KEYS);
                                                            if (branchTotals) Object.assign(branchSummary, branchTotals);
                                                            const isBranchApproved = pendingMarker(branchTotals, branchItems.find(a => a.isApproved === false));

                                                            return (
                                                                <div key={branch} className="expand-app-section">
                                                                    <div className={`report-details-summary-card branch ${isBranchApproved && !isBranchApproved?.isApproved ? "notApproved" : ""}`} {...rowToggleProps(expandedBranch === branch, () => toggleBranch(branch, branchItems))}>
                                                                        <div className="report-details-p center">
                                                                            <p><strong>{branch}</strong></p>
                                                                        </div>
                                                                        <div className="report-details-p">
                                                                            {` ${branchSummary.count}`}
                                                                        </div>
                                                                        <div className="report-details-p">
                                                                            {` ${formatter.format(branchSummary.orderAmount.toFixed(2))}`}
                                                                        </div>
                                                                        <div className="report-details-p">
                                                                            {` ${formatter.format(branchSummary.enduranceAmount.toFixed(2))}`}
                                                                        </div>
                                                                        <div className="report-details-p">
                                                                            {` ${formatter.format(branchSummary.refundAmount.toFixed(2))}`}
                                                                        </div>
                                                                    </div>

                                                                    {expandedBranch === branch && (
                                                                        <div className="expand-branches">
                                                                            {paged && <NodeState state={paged.leaf} />}
                                                                            {Object.entries(orderIds).map(([orderId, orderIdItems]) => {
                                                                                // Calculate orderId summary
                                                                                const orderIdSummary = orderIdItems.reduce((acc, item) => {
                                                                                    acc.count += 1;
                                                                                    acc.orderAmount += parseFloat(item[COMP_FIELDS2.orderAmount] || 0);
                                                                                    acc.enduranceAmount += parseFloat(item[COMP_FIELDS2.enduranceAmount] || 0);
                                                                                    acc.refundAmount += parseFloat(item[COMP_FIELDS2.refundAmount] || 0);
                                                                                    return acc;
                                                                                }, {
                                                                                    count: 0,
                                                                                    orderAmount: 0,
                                                                                    enduranceAmount: 0,
                                                                                    refundAmount: 0,
                                                                                });
                                                                                const orderIdDetails = orderIdItems[0]
                                                                                const editingThis = isEditing === orderIdDetails._id

                                                                                const isApproved = orderIdItems.find(a => a.isApproved === false)

                                                                                return (
                                                                                    <div key={orderId} className={`report-details-summary-card ${(has("report.approve")) ? "orderId" : ""} ${isApproved && !isApproved?.isApproved ? "notApproved" : ""}`}>
                                                                                        <div className="details-expand-card-col">
                                                                                            <div className="report-details-p center orderId">
                                                                                                <p><strong>{orderId}</strong></p>
                                                                                            </div>

                                                                                            <div className="report-details-p">
                                                                                                <p>
                                                                                                    {orderIdDetails[COMP_FIELDS2.date]} {orderIdDetails[COMP_FIELDS2.time]}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div className="report-details-p">
                                                                                                {editingThis
                                                                                                    ? <>
                                                                                                        <input
                                                                                                            aria-label={`${orderId} – ${getFieldLabel(lang, "orderStatus", AR_FIELD_LABELS)}`}
                                                                                                            className='details-expand-input'
                                                                                                            type="text"
                                                                                                            value={editItem[COMP_FIELDS2.orderStatus] || ""}
                                                                                                            onChange={(e) => handleChange(COMP_FIELDS2.orderStatus, e.target.value)}
                                                                                                        />
                                                                                                    </>
                                                                                                    : maps.orderStatus?.[(orderIdDetails[COMP_FIELDS2.orderStatus])] ?? (orderIdDetails[COMP_FIELDS2.orderStatus])
                                                                                                }
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="report-details-p col">
                                                                                            <strong>{translations[lang].reports.details.body[4]}</strong>
                                                                                            {editingThis ? (
                                                                                                <textarea className='card-edit-input textarea' aria-label={`${orderId} – ${getFieldLabel(lang, "cause", AR_FIELD_LABELS)}`} value={editItem[COMP_FIELDS2.cause] || ""} onChange={e => handleChange(COMP_FIELDS2.cause, e.target.value)} />
                                                                                            ) : (orderIdDetails[COMP_FIELDS2.cause])}
                                                                                        </div>
                                                                                        <div className="report-details-p">
                                                                                            {editingThis
                                                                                                ? <>
                                                                                                    <input
                                                                                                        aria-label={`${orderId} – ${getFieldLabel(lang, "orderAmount", AR_FIELD_LABELS)}`}
                                                                                                        className='details-expand-input'
                                                                                                        type="number"
                                                                                                        value={editItem[COMP_FIELDS2.orderAmount] || ""}
                                                                                                        onChange={(e) => handleChange(COMP_FIELDS2.orderAmount, e.target.value)}
                                                                                                    />
                                                                                                </>
                                                                                                : formatter.format(orderIdSummary.orderAmount.toFixed(2))
                                                                                            }
                                                                                        </div>
                                                                                        <div className="report-details-p">
                                                                                            {editingThis
                                                                                                ? <>
                                                                                                    <input
                                                                                                        aria-label={`${orderId} – ${getFieldLabel(lang, "enduranceAmount", AR_FIELD_LABELS)}`}
                                                                                                        className='details-expand-input'
                                                                                                        type="number"
                                                                                                        value={editItem[COMP_FIELDS2.enduranceAmount] || ""}
                                                                                                        onChange={(e) => handleChange(COMP_FIELDS2.enduranceAmount, e.target.value)}
                                                                                                    />
                                                                                                </>
                                                                                                : formatter.format(orderIdSummary.enduranceAmount.toFixed(2))
                                                                                            }
                                                                                        </div>
                                                                                        <div className="report-details-p">
                                                                                            {editingThis
                                                                                                ? <>
                                                                                                    <input
                                                                                                        aria-label={`${orderId} – ${getFieldLabel(lang, "refundAmount", AR_FIELD_LABELS)}`}
                                                                                                        className='details-expand-input'
                                                                                                        type="number"
                                                                                                        value={editItem[COMP_FIELDS2.refundAmount] || ""}
                                                                                                        onChange={(e) => handleChange(COMP_FIELDS2.refundAmount, e.target.value)}
                                                                                                    />
                                                                                                </>
                                                                                                : formatter.format(orderIdSummary.refundAmount.toFixed(2))
                                                                                            }
                                                                                        </div>

                                                                                        <div className="details-expand-card-body-btns">
                                                                                            {has("report.entry.update") && (editingThis ? (
                                                                                                <>
                                                                                                    <Button variant="primary"  appearance="filled" disabled={pending} onClick={handleSave}>
                                                                                                        {translations[lang].financial.save}
                                                                                                    </Button>
                                                                                                    <Button variant="danger" appearance="soft" onClick={handleCancel}>
                                                                                                        {translations[lang].financial.cancel}
                                                                                                    </Button>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <Button  variant="primary"
            appearance="soft"
            
            rounded   icon={<Repeat size={20} />}
            aria-label={`${text.edit}: ${orderId}`} title={text.edit}
            onClick={() => handleEdit(orderIdDetails)}>
                                                                                                       
                                                                                                    </Button>
                                                                                                </>
                                                                                            ))
                                                                                            }
                                                                                            {has("report.entry.delete") &&
                                                                                                <Button  variant="danger"
                appearance=""
               
               icon={<Trash2 size={20} />}
               aria-label={`${text.delete}: ${orderId}`} title={text.delete}
               onClick={() => confirmDelete(orderIdDetails._id)}>
                                                                                                   
                                                                                                </Button>
                                                                                            }
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            {paged?.leafFooter}
                                                                        </div>
                                                                    )}

                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                        )
                        }
                    </div>

                );
            })}
            {
                deleteTarget && (
                    <DeleteModal
                        cancel={() => setDeleteTarget(null)}
                        id={deleteTarget}
                        handleDelete={handleConfirmedDelete}
                    />
                )
            }
        </div >
    );
};
